import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import * as LocalStorage from "@omni/utils/localStorage.utils";
import { AxiosError, HttpStatusCode } from "axios";

import { createApiFor } from "@/api/hooks/utils";
import { addRecentSearch } from "@/components/Search/SearchBar/RecentSearches/recentSearches.source";
import {
    SearchConfiguration,
    SearchResults
} from "@/redux/modules/value/search/search.types";
import * as AlgoliaUserService from "@/services/algolia/algoliaUser.service";
import { applyResponseTransformers } from "@/services/bffSearch/applyTransformers";
import {
    CachedSearchResult,
    SearchCache
} from "@/services/bffSearch/bffSearchCache";
import BreakpointsService from "@/services/breakpoint.service";
import FeatureTogglesService from "@/services/featureToggle/featureToggles.service";
import { Config } from "@/utils/config.utils";
import { StorageKeys } from "@/utils/enums";
import {
    SearchContextEntryType,
    SearchRequest
} from "@/utils/search/types/searchRequest";
import { SearchResult } from "@/utils/search/types/searchResult";

const searchCache = new SearchCache();
const axios = createApiFor(`/${Config.general.basestore}`, { bff: true });

const search = (searchApiKey: string, request: SearchRequest) =>
    axios.post<SearchResult>(`/search`, request, {
        headers: {
            "x-algolia-api-key": searchApiKey,
            "x-algolia-usertoken":
                AlgoliaUserService.getAuthenticatedUserToken() ??
                AlgoliaUserService.getUserToken()
        }
    });

const cachedSearch =
    (searchApiKey: string) =>
    async (request: SearchRequest): Promise<CachedSearchResult> => {
        const cachedSearchResult = searchCache.get(request);
        if (cachedSearchResult) return cachedSearchResult;

        return search(searchApiKey, request)
            .catch(async (error: AxiosError) => {
                const statusCodes = [
                    error.status,
                    error.response?.status
                ].filter(isDefined);

                if (
                    !statusCodes.includes(HttpStatusCode.BadRequest) &&
                    !statusCodes.includes(HttpStatusCode.BadRequest)
                ) {
                    throw error;
                }

                const { appId, searchApiKey: newSearchApiKey } =
                    await AlgoliaUserService.generateSearchParameters(
                        window.storeManager.dispatch
                    );
                if (!appId || !newSearchApiKey) throw error;

                return search(newSearchApiKey, request);
            })
            .then((response) => {
                const searchResult: CachedSearchResult = {
                    status: response.status,
                    headers: response.headers,
                    data: response.data
                };

                searchCache.set(request, searchResult);

                return searchResult;
            });
    };

const debugConfig = () => {
    const debugConfig: Partial<SearchRequest> = {};
    if (FeatureTogglesService.isEnabled(FeatureToggleKey.DEBUG_ALGOLIA)) {
        debugConfig.debug = true;
    }

    const dynamicFacetNamesCount = attempt(
        () =>
            Number(
                LocalStorage.getItem(StorageKeys.AlgoliaDynamicFacetNamesAmount)
            ),
        null
    );

    if (dynamicFacetNamesCount) {
        debugConfig.params = {
            ...(debugConfig.params || []),
            dynamicFacets: {
                ...(debugConfig.params?.dynamicFacets || {}),
                amount: dynamicFacetNamesCount
            }
        };
    }

    const dynamicFacetNamesCutoff = attempt(
        () =>
            Number(
                LocalStorage.getItem(StorageKeys.AlgoliaDynamicFacetNamesCutoff)
            ),
        null
    );

    if (dynamicFacetNamesCutoff) {
        debugConfig.params = {
            ...(debugConfig.params || []),
            dynamicFacets: {
                ...(debugConfig.params?.dynamicFacets || {}),
                cutoff: dynamicFacetNamesCutoff
            }
        };
    }

    return debugConfig;
};

const bffSearch =
    (searchApiKey: string) =>
    async (
        configuration: SearchConfiguration
    ): Promise<
        | { type: "redirect"; url: string }
        | { type: "results"; results: SearchResults }
    > => {
        const searchRequest: SearchRequest = {
            clickAnalytics: true,
            context: {
                [SearchContextEntryType.DEVICE]: BreakpointsService.getDevice()
            },
            ...debugConfig(),
            ...configuration
        };

        const cachedSearchResult =
            await cachedSearch(searchApiKey)(searchRequest);

        const redirectUrl =
            cachedSearchResult.status === 303
                ? (cachedSearchResult.headers["x-hubo-location"] as string)
                : undefined;

        if (isDefined(redirectUrl)) {
            return {
                type: "redirect",
                url: redirectUrl
            };
        }

        if (searchRequest.query) {
            addRecentSearch({
                label: searchRequest.query,
                redirectUrl: redirectUrl
            });
        }

        return {
            type: "results",
            results: applyResponseTransformers(
                cachedSearchResult.data.results,
                configuration
            )
        };
    };

export { bffSearch };
